import { Namespace, TFunction } from "i18next";
import { useAtomValue } from "jotai";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { DataContext } from "@/provider/DataContextProvider";
import { useStartEmailVerification, useValidateOTP } from "@/services/api";
import {
  accessTokenAtom,
  AlertTypes,
  isAnonymousUserAtom,
} from "@/utils/atoms";
import { LOGIN_ERROR_PATTERNS } from "@/utils/loginErrorHandler";

import {
  TwoFactorConfirmationCodeForm,
  TwoFactorConfirmationInput,
} from "./AuthenticatorSetup/TwoFactorConfirmationInput";

interface Props {
  email: string;
  password?: string;
  handleClose: () => void;
  handleSuccess: () => void;
  alreadyAuthenticated?: boolean;
}

const getEmailVerificationError = (
  t: TFunction<Namespace, undefined, Namespace>,
  message: unknown,
) => {
  if (typeof message !== "string") {
    return t("translation:Common.generalForm.error.genericError");
  }
  if (message.includes(LOGIN_ERROR_PATTERNS.INVALID_VERIFICATION_CODE)) {
    return t("portal:authenticator.verifyEmail.error.invalidCode");
  }
  if (message.includes(LOGIN_ERROR_PATTERNS.VERIFICATION_LOCKED)) {
    return t("portal:authenticator.verifyEmail.error.maxRetry");
  }
  if (message.includes(LOGIN_ERROR_PATTERNS.VERIFICATION_CODE_LOCKED)) {
    return t("portal:authenticator.verifyEmail.error.maxCodes");
  }

  return t("translation:Common.generalForm.error.genericError");
};

export const VerifyEmailModal: FC<Props> = ({
  email,
  password,
  handleClose,
  handleSuccess,
  alreadyAuthenticated = false,
}) => {
  const { t } = useTranslation();
  const { loginAsCustomer } = useCustomerLogin();
  const showAlert = useAlert();
  const { setDataContext } = useContext(DataContext);
  const accessToken = useAtomValue(accessTokenAtom);
  const isAnonymousUser = useAtomValue(isAnonymousUserAtom);
  const methods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: { code: "" },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [error, setError] = useState<string>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = () => {
    // Clear any existing interval before starting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setCountdown(60);

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCountdown();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: startVerification } = useStartEmailVerification();
  const { mutateAsync: validateOTP } = useValidateOTP();

  const ensureAuthenticatedForOtp = async () => {
    const hasAuthenticatedCustomerSession =
      (alreadyAuthenticated || !!accessToken) && !isAnonymousUser;
    if (hasAuthenticatedCustomerSession) {
      return;
    }

    if (!password) {
      throw new Error("Password is required for email verification login");
    }

    await loginAsCustomer({
      email,
      password,
      skipEmailVerificationCheck: true,
    });

    // Keep a small delay for non-authenticated entry points to ensure auth state is propagated.
    await new Promise((resolve) => setTimeout(resolve, 2000));
  };

  const resendCode = async () => {
    setIsLoading(true);

    try {
      await ensureAuthenticatedForOtp();
      await startVerification();
      showAlert({
        type: AlertTypes.success,
        text: t("portal:authenticator.verifyEmail.successResend"),
      });
    } catch (e: any) {
      setError(getEmailVerificationError(t, e?.response?.data));
    } finally {
      setIsLoading(false);
      startCountdown();
    }
  };

  return (
    <FormProvider {...methods}>
      <Modal
        variant="dark"
        title={
          <h3 className="text-foreground-primary-inverse">
            {t("portal:authenticator.verifyEmail.confirmEmailAddress")}
          </h3>
        }
        onClose={() => {}}
        showCloseButton={false}
        hasScroll={false}
      >
        <form
          // The legacy global `form { color }` rule (src/styles/form.scss)
          // would otherwise be the inherited color for untinted text here;
          // this modal is always the dark variant, so pin it explicitly.
          className="flex flex-col gap-10 overflow-y-auto text-foreground-primary-inverse"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            methods.handleSubmit(async (data) => {
              setIsLoading(true);

              // To be able to send the validate request the user needs to be already logged in...
              try {
                await ensureAuthenticatedForOtp();

                // Validate OTP
                await validateOTP({
                  params: {
                    code: Number(data.code), // Safe cast, there won't be any leading zeros according to Compax
                  },
                });

                setDataContext((prev) => ({
                  ...prev,
                  isEmailVerified: true,
                }));

                showAlert({
                  type: AlertTypes.success,
                  text: t("portal:authenticator.verifyEmail.success"),
                });

                handleSuccess();
              } catch (e: any) {
                // On any failure, reset the code and show an error
                methods.reset({ code: "" });

                setError(getEmailVerificationError(t, e?.response?.data));
              } finally {
                setIsLoading(false);
              }
            })(e);
          }}
        >
          <div className="flex flex-col gap-6">
            <Trans
              i18nKey="portal:authenticator.verifyEmail.description"
              values={{
                email,
              }}
            />
            <div className="flex w-fit flex-col gap-2">
              <TwoFactorConfirmationInput
                error={error}
                clearError={() => setError(undefined)}
                variant="dark"
              />
              <Link
                size="sm"
                inverted
                onClick={async () => await resendCode()}
                disabled={countdown > 0 || isLoading}
              >
                {t("portal:authenticator.sms.verification.resend")}
                {countdown > 0 &&
                  ` ${t("portal:authenticator.sms.verification.secondsLeft", { count: countdown })}`}
              </Link>
            </div>
            <p className="flex flex-row flex-wrap items-center gap-2 text-left">
              <span>
                {t("portal:authenticator.verifyEmail.wrongMailAddress")}
              </span>
              <Link inverted onClick={() => handleClose()} disabled={isLoading}>
                {t("portal:authenticator.verifyEmail.enterEmailAgain")}
              </Link>
            </p>
          </div>

          <ButtonGroupFlexBox
            rightAligned
            forceButtonWidth
            primaryButton={
              <Button
                variant="primary"
                type="submit"
                inverted
                loading={isLoading}
                disabled={isLoading}
              >
                {t("portal:authenticator.verifyEmail.confirmCode")}
              </Button>
            }
          />
        </form>
      </Modal>
    </FormProvider>
  );
};
